import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
} from "@mui/material";
import { defineMessages, useIntl } from "react-intl";
import { useMemoryCreate } from "../../hooks/useMemoryCreate";
import { useSnackbar } from "@myplant-io/snackbar";

const messages = defineMessages({
  title: {
    id: "memory.create.title",
    defaultMessage: "Create New Memory",
  },
  textFieldLabel: {
    id: "memory.create.textFieldLabel",
    defaultMessage: "Memory Text",
  },
  textFieldHelperText: {
    id: "memory.create.textFieldHelperText",
    defaultMessage: "Enter the text you want to have remembered",
  },
  cancel: {
    id: "memory.create.cancel",
    defaultMessage: "Cancel",
  },
  create: {
    id: "memory.create.create",
    defaultMessage: "Create",
  },
  successMessage: {
    id: "memory.create.successMessage",
    defaultMessage: "Memory created successfully",
  },
  errorMessage: {
    id: "memory.create.errorMessage",
    defaultMessage: "Failed to create memory",
  },
});

interface CreateMemoryDialogProps {
  open: boolean;
  onClose: () => void;
}

export function CreateMemoryDialog({ open, onClose }: CreateMemoryDialogProps) {
  const { formatMessage: t } = useIntl();
  const [text, setText] = useState("");
  const { mutateAsync: createMemory, isLoading } = useMemoryCreate();
  const { showSuccessMessage, showErrorMessage } = useSnackbar();

  const handleCreate = async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      return;
    }

    try {
      await createMemory(trimmed);
      showSuccessMessage({
        message: t(messages.successMessage),
      });
      setText("");
      onClose();
    } catch (error) {
      showErrorMessage({
        message: t(messages.errorMessage),
      });
    }
  };

  const handleClose = () => {
    setText("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{t(messages.title)}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          multiline
          rows={4}
          fullWidth
          label={t(messages.textFieldLabel)}
          helperText={t(messages.textFieldHelperText)}
          value={text}
          onChange={(e) => setText(e.target.value)}
          variant="outlined"
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {t(messages.cancel)}
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={!text.trim() || isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {t(messages.create)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
